import { useMemo } from 'react'

import { FvButton } from '@fv/client-components'
import {
  type ChargeModel,
  currencyToString,
  getManualChargeOptions,
  type LocationLike,
  parseCurrency,
} from '@fv/client-core'
import type { ChargeName, Currency } from '@fv/models'

import { InputAdornment, InputGroup } from '@/components/inputs/InputGroup'
import { SelectField } from '@/components/inputs/SelectField'
import { Pinline } from '@/components/Pinline'

type Props = {
  charge: ChargeModel
  currency: Currency
  locations: LocationLike[] | undefined
  onChange: (charge: ChargeModel) => void
  onRemove: () => void
}

const parseChargeAmount = (name: ChargeName, amount: number) => {
  if (name === 'discount') {
    return -Math.abs(amount)
  }
  return Math.abs(amount)
}

export const ChargeField = ({
  charge,
  currency,
  locations,
  onChange,
  onRemove,
}: Props) => {
  const localChargeOptions = useMemo(
    () => getManualChargeOptions(currency, locations),
    [currency, locations],
  )

  const handleEdit = <T extends keyof ChargeModel>(
    key: T,
    value: ChargeModel[T],
  ) => {
    onChange({
      ...charge,
      [key]: value,
      ...(key === 'amount' && {
        amount: parseChargeAmount(charge.name, value as number),
      }),
      ...(key === 'name' && {
        amount: parseChargeAmount(value as ChargeName, charge.amount),
      }),
    })
  }

  return (
    <div className="flex items-center gap-3">
      <SelectField
        className="w-auto"
        name="charge-name"
        onChange={e => handleEdit('name', e.target.value as ChargeName)}
        options={localChargeOptions}
        value={charge.name}
        required
      />

      {charge.name === 'other' && (
        <InputGroup
          className="mb-0"
          inputProps={{
            name: 'charge-description',
            value: charge.providerDetail || '',
            onChange: e => handleEdit('providerDetail', e.target.value),
            placeholder: 'Enter charge description',
          }}
          inputType="text"
          required
        />
      )}
      <Pinline dotted flex />
      <FvButton
        icon="trash"
        iconClass="text-fv-orange-bright"
        onClick={onRemove}
      />

      <InputGroup
        className="mb-0 w-32"
        inputProps={{
          name: 'charge-amount',
          isPrice: true,
          value: charge.amount ? currencyToString(charge.amount) : '',
          className: 'text-right',
          onChange: e => {
            const amount = parseCurrency(e.target.value)
            handleEdit(
              'amount',
              isNaN(amount) ? 0 : parseChargeAmount(charge.name, amount),
            )
          },
          min: 1,
        }}
        inputType="amount"
        required
        startContent={<InputAdornment icon="dollar-sign" position="start" />}
      />
    </div>
  )
}
